import { AbsgArrayHelper, HttpExtra, LogService } from '@absg/angular-common';
import { MSODataQuery, ODataResult } from '@absg/angular-components';
import {
  BinProductDetail,
  FormularyApi,
  FormularyODataApi,
  GetProductRuleValueRequest,
  MedicalEquivalentApi,
  MedicallyEquivalentGroupApi,
  ODataResultProductDetail,
  ODataResultRule,
  OrderWithMatchingDrugs,
  PracticeProductsRequest,
  ProductDetail,
  Rule,
  RuleODataApi,
  RuleProductSettingApi,
  RuleProductSettingDetail,
  SupplierProduct,
  SupplierProductODataApi,
  SupplierProductRuleXrefLite,
} from '@absg/api-formulary';
import { GetAssignedItemByOrderMatchingRequest } from '@absg/api-formulary/model/getAssignedItemByOrderMatchingRequest';
import { BaseService, ScannerSearchHelper } from '@absg/nim-client-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StationInfoService } from 'core.services/station-info.service';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class FormularyService extends BaseService {

  constructor(private formularyApi: FormularyApi,
    private formularyODataApi: FormularyODataApi,
    private ruleProductSettingApi: RuleProductSettingApi,
    private ruleODataApi: RuleODataApi,
    private supplierProductODataApi: SupplierProductODataApi,
    private medicalEquivalentApi: MedicalEquivalentApi,
    private stationInfoService: StationInfoService,
    private medicallyEquivalentGroupApi: MedicallyEquivalentGroupApi,
    loggingService: LogService,
  ) {
    super(loggingService);
  }

  getAssignedItemsByOrderMatching(request: GetAssignedItemByOrderMatchingRequest): Observable<OrderWithMatchingDrugs[]> {
    return this.formularyApi.getAssignedItemsByOrderMatchingV2(request)
      .pipe(catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<OrderWithMatchingDrugs[]>;
  }

  getSupplierProducts(extraHttpOptions?: HttpExtra): Observable<ODataResult<SupplierProduct>> {
    return this.supplierProductODataApi.getSupplierProductsOData(extraHttpOptions)
      .pipe(
        map((response) => ODataResult.convertFromMSODataResult(response as ODataResult<SupplierProduct>)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }
  getBinDispenseType(meiParentId: number): Observable<number> {
    return this.medicallyEquivalentGroupApi.getBinDispenseType(meiParentId)
      .pipe(catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<number>;
  }

  getUnAssignedPracticeFormularyProducts(practiceId: number, supplierProductInclusionIds: number[],
    extraHttpRequestParams?: HttpExtra): Observable<ODataResult<ProductDetail>> {
    return this.formularyODataApi.getUnAssignedPracticeFormularyProductsOData(practiceId, { supplierProductInclusionIds } as PracticeProductsRequest, extraHttpRequestParams)
      .pipe(
        map(result => ODataResult.convertFromMSODataResult(result as ODataResult<ProductDetail>)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getRulesForSupplierProductInclusion(supplierProductInclusionId: number[]): Observable<RuleProductSettingDetail[]> {
    if (supplierProductInclusionId.length === 0) {
      return of([]);
    }
    return this.ruleProductSettingApi.getRulesForSupplierProducts(supplierProductInclusionId)
      .pipe(catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)));
  }

  pluralizeUnitOfMeasureAbbreviation(uomAbbreviation: string, quantity: number): string {
    if (quantity > 0 && uomAbbreviation !== 'cs' && uomAbbreviation !== 'ea' && uomAbbreviation !== 'bx' && uomAbbreviation !== 'syr') {
      uomAbbreviation += 's';
    }
    return uomAbbreviation;
  }

  getRuleSettings(): Observable<Rule[]> {
    const query = new MSODataQuery({
      searchFields: [],
      expandFields: ['ruleType', 'ruleCategory'],
      pageNumber: 1,
      itemsPerPage: 100,
      excludeCount: true,
    });
    return this.ruleODataApi.getRuleSettingsOData({ search: query.toString() })
      .pipe(
        map((response: ODataResultRule) => response.value as Rule[]),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getProductById(productId: number): Observable<ProductDetail> {
    const httpParams = {};
    return this.formularyApi.getProductById(productId, httpParams)
      .pipe(
        map((response: ProductDetail) => response as ProductDetail),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getProductXrefs(): Observable<SupplierProductRuleXrefLite[]> {
    return this.ruleProductSettingApi.getProductXrefs()
      .pipe(
        map((response) => response as SupplierProductRuleXrefLite[]),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getScannerNdcOptions(tenDigitNdc: string, practiceId: number, assignedItems: number[], patientOwned = false): Observable<ODataResultProductDetail> {
    let query = '';
    if (!tenDigitNdc) { return of(null); }
    if (tenDigitNdc !== null) {
      const ndcOptionList = ScannerSearchHelper.generateNdcConversationOptions(tenDigitNdc);
      AbsgArrayHelper.each(ndcOptionList, (ndc, index) => {
        if (index === 0) {
          query = `unitNdc eq '${ScannerSearchHelper.addNdcFormat(ndc)}'`;
        } else {
          query += ` or unitNdc eq '${ScannerSearchHelper.addNdcFormat(ndc)}'`;
        }
        query += ` or packageNdc eq '${ScannerSearchHelper.addNdcFormat(ndc)}'`;
      });
    }

    if (!patientOwned) {
      query = `(${query}) and (isDiscontinued eq false)`;
    }

    if (!query) { return of(null); }
    // service will get available formulary items using practice id and the items assigned on the station.
    return this.getUnAssignedPracticeFormularyProducts(practiceId, assignedItems, { search: { $filter: query } });
  }

  getBinGroupDetails(): Observable<ProductDetail[]> {
    return this.medicalEquivalentApi.getBinGroupDetailByStation(this.stationInfoService.getStationId())
      .pipe(
        take(1),
        map(response => response as BinProductDetail[]),
        catchError((response: HttpErrorResponse) => {
          this.handleError(response);
          return of([]);
        }),
      );
  }

  getProductRuleXref(request: GetProductRuleValueRequest): Observable<SupplierProductRuleXrefLite[]> {
    return this.ruleProductSettingApi.getProductRuleXrefs(request)
      .pipe(
        take(1),
        map((response) => response as SupplierProductRuleXrefLite[]),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)));
  }
}
